import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Cropper from 'react-easy-crop';
import { Check, RotateCw, X } from 'lucide-react';
import { Button } from 'src/@/components/ui/button';
import { Slider } from 'src/@/components/ui/slider';
import DrawingCanvas from './DrawingCanvas';

// ----------------------------------------------------------------------
// Palco da imagem: visualização, recorte/rotação (react-easy-crop) e desenho.
// No modo view/draw a imagem é dimensionada por cálculo de "contain" explícito
// para que o canvas de desenho cubra exatamente a área visível da imagem.

const ASPECT_PRESETS = [
    { key: 'original', label: null },
    { key: '1:1', value: 1 },
    { key: '4:3', value: 4 / 3 },
    { key: '16:9', value: 16 / 9 },
];

function CropStage({ imageUrl, onApplyCrop, onCancelCrop }) {
    const { t } = useTranslation('chat');
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [originalAspect, setOriginalAspect] = useState(null);
    const [aspectKey, setAspectKey] = useState('original');

    useEffect(() => {
        const img = new Image();
        img.onload = () => setOriginalAspect(img.naturalWidth / img.naturalHeight);
        img.src = imageUrl;
    }, [imageUrl]);

    const preset = ASPECT_PRESETS.find((item) => item.key === aspectKey);
    const aspect = (preset?.value ?? originalAspect) || 4 / 3;

    return (
        <div className="relative w-full h-full">
            <Cropper
                image={imageUrl}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={aspect}
                showGrid
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={(_, areaPixels) => setCroppedAreaPixels(areaPixels)}
            />

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 px-4 py-2.5 rounded-xl bg-white/95 dark:bg-slate-800/95 border border-gray-200 dark:border-slate-700 shadow-lg">
                <div className="flex items-center gap-1">
                    {ASPECT_PRESETS.map((item) => (
                        <button
                            key={item.key}
                            type="button"
                            onClick={() => setAspectKey(item.key)}
                            className={`px-2 py-1 rounded-md text-[11px] font-semibold transition-colors ${
                                aspectKey === item.key
                                    ? 'bg-violet-100 text-[#7C3AED] dark:bg-violet-500/20'
                                    : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'
                            }`}
                        >
                            {item.key === 'original' ? t('mediaEditor.aspectOriginal') : item.key}
                        </button>
                    ))}
                </div>

                <div className="w-px h-6 bg-gray-200 dark:bg-slate-700" />
                <div className="flex items-center gap-2 w-44">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                        {t('mediaEditor.zoom')}
                    </span>
                    <Slider
                        value={[zoom]}
                        min={1}
                        max={3}
                        step={0.05}
                        onValueChange={(value) => setZoom(value[0])}
                        className="flex-1"
                    />
                </div>

                <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    title={t('mediaEditor.rotate')}
                    onClick={() => setRotation((prev) => (prev + 90) % 360)}
                >
                    <RotateCw className="h-4 w-4" />
                </Button>

                <div className="w-px h-6 bg-gray-200 dark:bg-slate-700" />

                <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    title={t('mediaEditor.cancelCrop')}
                    onClick={onCancelCrop}
                >
                    <X className="h-4 w-4" />
                </Button>
                <Button
                    size="icon"
                    className="h-8 w-8 rounded-lg bg-[#7C3AED] hover:bg-[#6d28d9] text-white"
                    title={t('mediaEditor.applyCrop')}
                    onClick={() => onApplyCrop({ rotation, croppedAreaPixels })}
                >
                    <Check className="h-4 w-4" />
                </Button>
            </div>
        </div>
    );
}

export default function ImageEditorStage({ item, mode, drawColor, onStrokesChange, onApplyCrop, onCancelCrop }) {
    const [containerNode, setContainerNode] = useState(null);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
    const [naturalSize, setNaturalSize] = useState(null);

    const imageUrl = item.editedUrl || item.originalUrl;

    useEffect(() => {
        let cancelled = false;
        setNaturalSize(null);
        const img = new Image();
        img.onload = () => {
            if (!cancelled) {
                setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
            }
        };
        img.src = imageUrl;
        return () => {
            cancelled = true;
        };
    }, [imageUrl]);

    // Callback ref: o container é desmontado ao entrar no modo crop, então a
    // observação precisa ser refeita a cada remontagem do nó.
    useEffect(() => {
        if (!containerNode) return undefined;
        const measure = () =>
            setContainerSize({ width: containerNode.clientWidth, height: containerNode.clientHeight });
        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(containerNode);
        return () => observer.disconnect();
    }, [containerNode]);

    if (mode === 'crop') {
        return <CropStage key={imageUrl} imageUrl={imageUrl} onApplyCrop={onApplyCrop} onCancelCrop={onCancelCrop} />;
    }

    let fitted = null;
    if (naturalSize && containerSize.width > 0 && containerSize.height > 0) {
        const scale = Math.min(
            containerSize.width / naturalSize.width,
            containerSize.height / naturalSize.height,
            1
        );
        fitted = {
            width: Math.round(naturalSize.width * scale),
            height: Math.round(naturalSize.height * scale),
        };
    }

    return (
        <div ref={setContainerNode} className="w-full h-full flex items-center justify-center overflow-hidden">
            {fitted ? (
                <div className="relative" style={{ width: fitted.width, height: fitted.height }}>
                    <img
                        src={imageUrl}
                        alt={item.file?.name || ''}
                        className="select-none rounded-lg w-full h-full"
                        draggable={false}
                    />
                    {(mode === 'draw' || item.strokes.length > 0) && (
                        <DrawingCanvas
                            strokes={item.strokes}
                            color={drawColor}
                            onStrokesChange={onStrokesChange}
                            readOnly={mode !== 'draw'}
                        />
                    )}
                </div>
            ) : (
                // Fallback enquanto não há medição: nunca deixar a imagem invisível
                <img
                    src={imageUrl}
                    alt={item.file?.name || ''}
                    className="select-none rounded-lg max-w-full max-h-full object-contain"
                    draggable={false}
                />
            )}
        </div>
    );
}
